import { FC } from 'react';
import styled from 'styled-components';
import Button from '../Button';
import CrossIcon from '../icons/Cross';

type DivProps = {
  open: boolean;
} & React.HTMLProps<HTMLDivElement>;
/* eslint-disable @typescript-eslint/no-unused-vars */
const DivWithProps: FC<DivProps> = ({ open, ...props }) => {
  return <div {...props} />;
};
const Container = styled(DivWithProps)`
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  z-index: 50;
  pointer-events: none;
`;
const Background = styled(DivWithProps)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--gray-800);
  opacity: 0.4;
  pointer-events: painted;
  display: ${({ open }) => (open ? 'block' : 'none')};
`;

const ContentContainer = styled(DivWithProps)`
  display: flex;
  flex-direction: column;
  background: white;
  position: fixed;
  top: 0;
  bottom: 0;
  right: ${({ open }) => (open ? '0' : '-100%')};
  height: 100%;
  pointer-events: painted;
  transition: 0.2s;
  width: min(25rem, 100vw);
  overflow-y: auto;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  pointer-events: painted;
  padding: 1.5rem;
  width: 100%;
  overflow-y: auto;
`;

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  stroke: var(--gray-500);

  svg {
    cursor: pointer;
  }
`;

const Title = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1.5rem;

  h1 {
    color: var(--gray-900);
    font-size: 1.25rem;
    font-weight: 500;
  }

  p {
    color: var(--gray-500);
    font-size: 0.9rem;
    font-weight: 400;
  }
`;

const Foot = styled.div`
  position: sticky;
  top: 100vh;
  bottom: 0;
  left: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--gray-200);
  & > * {
    margin-left: 0.75rem;
  }
  background: var(--gray-contrast);
`;

type Props = {
  open: boolean;
  close: () => void;
};
const Drawer: FC<Props> = ({ children, open, close }) => {
  return (
    <Container open={open}>
      <Background open={open} onClick={close} />
      <ContentContainer open={open}>{children}</ContentContainer>
    </Container>
  );
};

type ActionDrawerProps = Props & {
  title: string;
  subtitle: string;
  cancel: () => void;
  apply: () => void;
  applyText?: string;
};

export const ActionDrawer: FC<ActionDrawerProps> = ({
  children,
  open,
  close,
  title,
  subtitle,
  cancel,
  apply,
  applyText,
}) => {
  return (
    <Drawer open={open} close={close}>
      <Content>
        <Head>
          <Title>
            <h1>{title}</h1>
            <p>{subtitle}</p>
          </Title>
          <div onClick={close}>
            <CrossIcon />
          </div>
        </Head>
        {children}
      </Content>
      <Foot>
        <Button
          variant="outlined"
          onClick={() => {
            cancel();
            close();
          }}
        >
          Annuler
        </Button>
        <Button
          color="primary"
          onClick={() => {
            apply();
            close();
          }}
        >
          {applyText || 'Appliquer'}
        </Button>
      </Foot>
    </Drawer>
  );
};

export default Drawer;
